import type {
  AnnouncementDto,
  AuditLogDto,
  CompetitionCategoryDto,
  CompetitionDetailDto,
  CompetitionRoleDto,
  CompetitionSettingsDto,
  CompetitionSummaryDto,
  CreateCompetitionCategoryRequest,
  CreateCompetitionRequest,
  CreateRoundAdvancementsRequest,
  CreateRoundRequest,
  CreateTeamInviteRequest,
  DiscoveryCategoryDto,
  GrantCompetitionRoleRequest,
  RoundAdvancementDto,
  RoundDto,
  TeamDetailDto,
  TeamDto,
  TeamInviteCreatedDto,
  TeamInviteDto,
  RegistrationReminderResultDto,
  TeamOperationsRowDto,
  TransferTeamLeaderRequest,
  UpdateCompetitionCategoryRequest,
  UpdateCompetitionRequest,
  UpdateCompetitionSettingsRequest,
  UpdateCompetitionStatusRequest,
  UpdateRoundRequest,
} from '@napaicp/schemas'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { apiRequest, apiRequestWithMeta } from '@/lib/api-client'
import { competitionKeys } from './query-keys'

const base = (competitionId: string) => `/api/competitions/${competitionId}`

export function useCompetitions(): UseQueryResult<CompetitionSummaryDto[]> {
  return useQuery({
    queryKey: competitionKeys.list(),
    queryFn: () => apiRequest<CompetitionSummaryDto[]>('/api/competitions'),
  })
}

export function useCompetition(competitionId: string): UseQueryResult<CompetitionDetailDto> {
  return useQuery({
    queryKey: competitionKeys.detail(competitionId),
    queryFn: () => apiRequest<CompetitionDetailDto>(base(competitionId)),
    enabled: competitionId.length > 0,
  })
}

export function useCreateCompetition() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateCompetitionRequest) =>
      apiRequest<CompetitionDetailDto>('/api/competitions', { method: 'POST', body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: competitionKeys.all })
    },
  })
}

export function useUpdateCompetition(competitionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateCompetitionRequest) =>
      apiRequest<CompetitionDetailDto>(base(competitionId), { method: 'PATCH', body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: competitionKeys.detail(competitionId) })
      await queryClient.invalidateQueries({ queryKey: competitionKeys.list() })
    },
  })
}

export interface CompetitionLogoUploadIntentResult {
  assetId: string
  uploadUrl: string
  requiredHeaders: Record<string, string>
  expiresAt: string
}

/** Creates an `uploaded`-status asset row and returns the presigned R2 PUT URL — SDD §12 steps 1-2. */
export function useInitCompetitionLogoUpload(competitionId: string) {
  return useMutation({
    mutationFn: (input: { filename: string; contentType: string; sizeBytes: number }) =>
      apiRequest<CompetitionLogoUploadIntentResult>(`${base(competitionId)}/logo/uploads`, {
        method: 'POST',
        body: input,
      }),
  })
}

/** Confirms the bytes landed in R2 — flips the asset to `ready`, usable by a subsequent Save. Does not touch the competition's official logo (SDD §12 step 5: pending only). */
export function useFinalizeCompetitionLogoUpload(competitionId: string) {
  return useMutation({
    mutationFn: (input: {
      assetId: string
      etag: string | null
      width: number | null
      height: number | null
    }) =>
      apiRequest<{ logo: CompetitionSummaryDto['logo'] }>(
        `${base(competitionId)}/logo/uploads/${input.assetId}/complete`,
        { method: 'POST', body: { etag: input.etag, width: input.width, height: input.height } },
      ),
  })
}

export function useUpdateCompetitionStatus(competitionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateCompetitionStatusRequest) =>
      apiRequest<CompetitionDetailDto>(`${base(competitionId)}/status`, {
        method: 'PUT',
        body,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: competitionKeys.detail(competitionId) })
      await queryClient.invalidateQueries({ queryKey: competitionKeys.list() })
    },
  })
}

export function useCompetitionCategories(
  competitionId: string,
): UseQueryResult<CompetitionCategoryDto[]> {
  return useQuery({
    queryKey: competitionKeys.categories(competitionId),
    queryFn: () => apiRequest<CompetitionCategoryDto[]>(`${base(competitionId)}/categories`),
    enabled: competitionId.length > 0,
  })
}

export function useCreateCategory(competitionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateCompetitionCategoryRequest) =>
      apiRequest<CompetitionCategoryDto>(`${base(competitionId)}/categories`, {
        method: 'POST',
        body,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: competitionKeys.categories(competitionId) })
    },
  })
}

export function useUpdateCategory(competitionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      categoryId,
      body,
    }: {
      categoryId: string
      body: UpdateCompetitionCategoryRequest
    }) =>
      apiRequest<CompetitionCategoryDto>(`${base(competitionId)}/categories/${categoryId}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: competitionKeys.categories(competitionId) })
    },
  })
}

export function useArchiveCategory(competitionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (categoryId: string) =>
      apiRequest<CompetitionCategoryDto>(`${base(competitionId)}/categories/${categoryId}`, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: competitionKeys.categories(competitionId) })
    },
  })
}

/** PR-131 — the platform Discovery Categories this competition currently belongs to. */
export function useCompetitionDiscoveryCategories(
  competitionId: string,
): UseQueryResult<DiscoveryCategoryDto[]> {
  return useQuery({
    queryKey: competitionKeys.discoveryCategories(competitionId),
    queryFn: () =>
      apiRequest<DiscoveryCategoryDto[]>(`${base(competitionId)}/discovery-categories`),
    enabled: competitionId.length > 0,
  })
}

/** Full-replace (PR-131/API-068): `categoryIds` becomes exactly the competition's category set. */
export function useSetCompetitionDiscoveryCategories(competitionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (categoryIds: string[]) =>
      apiRequest<DiscoveryCategoryDto[]>(`${base(competitionId)}/discovery-categories`, {
        method: 'PUT',
        body: { categoryIds },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: competitionKeys.discoveryCategories(competitionId),
      })
    },
  })
}

export function roundsQueryOptions(competitionId: string) {
  return {
    queryKey: competitionKeys.rounds(competitionId),
    queryFn: () => apiRequest<RoundDto[]>(`${base(competitionId)}/rounds`),
    enabled: competitionId.length > 0,
  }
}

export function useRounds(competitionId: string): UseQueryResult<RoundDto[]> {
  return useQuery(roundsQueryOptions(competitionId))
}

/**
 * UX-290 (Student Participant UI/UX/IA v4.45, Wave G): the existing
 * `GET /:competitionId/announcements` route (only `competition.read`,
 * which every competition role — including `student` — already holds) had
 * no web caller at all before this hook. Reused as-is, no new endpoint.
 */
export function useAnnouncements(competitionId: string): UseQueryResult<AnnouncementDto[]> {
  return useQuery({
    queryKey: competitionKeys.announcements(competitionId),
    queryFn: () => apiRequest<AnnouncementDto[]>(`${base(competitionId)}/announcements`),
    enabled: competitionId.length > 0,
  })
}

export function useCreateRound(competitionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateRoundRequest) =>
      apiRequest<RoundDto>(`${base(competitionId)}/rounds`, { method: 'POST', body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: competitionKeys.rounds(competitionId) })
    },
  })
}

export function useUpdateRound(competitionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ roundId, body }: { roundId: string; body: UpdateRoundRequest }) =>
      apiRequest<RoundDto>(`${base(competitionId)}/rounds/${roundId}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: competitionKeys.rounds(competitionId) })
    },
  })
}

export function useRoundAdvancements(
  competitionId: string,
  roundId: string | undefined,
): UseQueryResult<RoundAdvancementDto[]> {
  return useQuery({
    queryKey: competitionKeys.roundAdvancements(competitionId, roundId ?? ''),
    queryFn: () =>
      apiRequest<RoundAdvancementDto[]>(`${base(competitionId)}/rounds/${roundId}/advancements`),
    enabled: competitionId.length > 0 && roundId !== undefined,
  })
}

/**
 * Whether the caller's own team has advanced to `roundId` — `null` is a
 * normal, common answer ("not advanced yet"), not an error. Used to decide
 * whether to show a round's submission entry point to a student at all.
 */
export function useMyRoundAdvancement(
  competitionId: string,
  roundId: string | undefined,
): UseQueryResult<RoundAdvancementDto | null> {
  return useQuery({
    queryKey: competitionKeys.myRoundAdvancement(competitionId, roundId ?? ''),
    queryFn: () =>
      apiRequest<RoundAdvancementDto | null>(
        `${base(competitionId)}/rounds/${roundId}/advancements/me`,
      ),
    enabled: competitionId.length > 0 && roundId !== undefined,
  })
}

export function useCreateRoundAdvancements(competitionId: string, roundId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateRoundAdvancementsRequest) =>
      apiRequest<RoundAdvancementDto[]>(`${base(competitionId)}/rounds/${roundId}/advancements`, {
        method: 'POST',
        body,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: competitionKeys.roundAdvancements(competitionId, roundId),
      })
    },
  })
}

export function useCompetitionRoles(competitionId: string): UseQueryResult<CompetitionRoleDto[]> {
  return useQuery({
    queryKey: competitionKeys.roles(competitionId),
    queryFn: () => apiRequest<CompetitionRoleDto[]>(`${base(competitionId)}/roles`),
    enabled: competitionId.length > 0,
  })
}

export function useGrantRole(competitionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: GrantCompetitionRoleRequest) =>
      apiRequest<CompetitionRoleDto>(`${base(competitionId)}/roles`, { method: 'POST', body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: competitionKeys.roles(competitionId) })
    },
  })
}

export function useRevokeRole(competitionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (roleId: string) =>
      apiRequest<{ id: string }>(`${base(competitionId)}/roles/${roleId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: competitionKeys.roles(competitionId) })
    },
  })
}

/**
 * Settings is gated at `competition.manage` server-side, so this is only
 * worth calling once the caller is known to hold it — otherwise every
 * non-admin viewer of the Overview page would generate a guaranteed 403.
 */
export function useCompetitionSettings(
  competitionId: string,
  enabled = true,
): UseQueryResult<CompetitionSettingsDto> {
  return useQuery({
    queryKey: competitionKeys.settings(competitionId),
    queryFn: () => apiRequest<CompetitionSettingsDto>(`${base(competitionId)}/settings`),
    enabled: competitionId.length > 0 && enabled,
  })
}

export function useUpdateSettings(competitionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateCompetitionSettingsRequest) =>
      apiRequest<CompetitionSettingsDto>(`${base(competitionId)}/settings`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: competitionKeys.settings(competitionId) })
    },
  })
}

export interface AuditLogFilters {
  action?: string
  /** UX-108 #3/AC-160: server-side event-category filter. */
  category?: string
  actorId?: string
  entityType?: string
  entityId?: string
  from?: string
  to?: string
}

export interface AuditLogPage {
  rows: AuditLogDto[]
  /** Cursor for the next page, or `null` when this page was not full. */
  nextBefore: string | null
}

const AUDIT_LOG_PAGE_SIZE = 25

/**
 * UX-108: cursor-paginated via the existing `nextBefore`/`before`/`limit`
 * contract on `GET .../audit-logs` (no new API surface). `useInfiniteQuery`
 * accumulates pages itself, keyed by the `before` cursor, so a plain filter
 * change (which changes `queryKey`) starts a fresh page 1 automatically.
 */
export function useAuditLogs(
  competitionId: string,
  filters: AuditLogFilters = {},
): UseInfiniteQueryResult<InfiniteData<AuditLogPage, string | undefined>> {
  const keyFilters: Record<string, string> = {
    action: filters.action ?? '',
    category: filters.category ?? '',
    actorId: filters.actorId ?? '',
    entityType: filters.entityType ?? '',
    entityId: filters.entityId ?? '',
    from: filters.from ?? '',
    to: filters.to ?? '',
  }

  return useInfiniteQuery({
    queryKey: competitionKeys.auditLogs(competitionId, keyFilters),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const search = new URLSearchParams()
      for (const key of Object.keys(filters) as (keyof AuditLogFilters)[]) {
        const value = filters[key]
        if (value === undefined || value === '') continue
        search.set(key, value)
      }
      search.set('limit', String(AUDIT_LOG_PAGE_SIZE))
      if (pageParam !== undefined) search.set('before', pageParam)

      const { data, meta } = await apiRequestWithMeta<AuditLogDto[]>(
        `${base(competitionId)}/audit-logs?${search.toString()}`,
      )
      const nextBefore = meta.nextBefore
      return { rows: data, nextBefore: typeof nextBefore === 'string' ? nextBefore : null }
    },
    getNextPageParam: (lastPage) => lastPage.nextBefore ?? undefined,
    enabled: competitionId.length > 0,
  })
}

// ---------------------------------------------------------------------------
// Teams (admin/staff cross-team view — `team.read.all`/`team.manage`, gated
// server-side; the routes are the same ones a team leader uses on their own
// team, `requireTeamMembership()` already grants staff/admin access to any
// team in the competition).
// ---------------------------------------------------------------------------

export interface TeamPage {
  rows: TeamDto[]
  total: number
  limit: number
  offset: number
}

export function useTeams(
  competitionId: string,
  search: string,
  page: { limit: number; offset: number },
): UseQueryResult<TeamPage> {
  const params = new URLSearchParams({
    limit: String(page.limit),
    offset: String(page.offset),
  })
  if (search.trim().length > 0) params.set('search', search.trim())

  return useQuery({
    queryKey: competitionKeys.teams(competitionId, {
      search: search.trim(),
      limit: String(page.limit),
      offset: String(page.offset),
    }),
    queryFn: async () => {
      const { data, meta } = await apiRequestWithMeta<TeamDto[]>(
        `${base(competitionId)}/teams?${params.toString()}`,
      )
      return {
        rows: data,
        total: Number(meta.total ?? data.length),
        limit: Number(meta.limit ?? page.limit),
        offset: Number(meta.offset ?? page.offset),
      }
    },
    enabled: competitionId.length > 0,
  })
}

export interface TeamOperationsPage {
  rows: TeamOperationsRowDto[]
  total: number
  limit: number
  offset: number
}

/**
 * SDD v1.2 §9/§34 Team Operations Matrix (Wave B). Same `GET .../teams`
 * endpoint `useTeams` calls, opted into the richer per-team aggregate
 * projection via `include=operations` — never a second endpoint (spec §18
 * "MUST NOT duplicate API"). `status` is the registration-completion filter
 * (`complete`/`incomplete`/`cancelled`) the Operations Dashboard's
 * drill-down links use.
 */
export function useTeamOperations(
  competitionId: string,
  filters: { search: string; status?: 'complete' | 'incomplete' | 'cancelled' },
  page: { limit: number; offset: number },
): UseQueryResult<TeamOperationsPage> {
  const params = new URLSearchParams({
    include: 'operations',
    limit: String(page.limit),
    offset: String(page.offset),
  })
  if (filters.search.trim().length > 0) params.set('search', filters.search.trim())
  if (filters.status !== undefined) params.set('status', filters.status)

  return useQuery({
    queryKey: competitionKeys.teams(competitionId, {
      include: 'operations',
      search: filters.search.trim(),
      status: filters.status ?? '',
      limit: String(page.limit),
      offset: String(page.offset),
    }),
    queryFn: async () => {
      const { data, meta } = await apiRequestWithMeta<TeamOperationsRowDto[]>(
        `${base(competitionId)}/teams?${params.toString()}`,
      )
      return {
        rows: data,
        total: Number(meta.total ?? data.length),
        limit: Number(meta.limit ?? page.limit),
        offset: Number(meta.offset ?? page.offset),
      }
    },
    enabled: competitionId.length > 0,
  })
}

export function useTeamDetail(
  competitionId: string,
  teamId: string | null,
): UseQueryResult<TeamDetailDto> {
  return useQuery({
    queryKey: competitionKeys.teamDetail(competitionId, teamId ?? ''),
    queryFn: () => apiRequest<TeamDetailDto>(`${base(competitionId)}/teams/${teamId}`),
    enabled: competitionId.length > 0 && teamId !== null,
  })
}

/**
 * SDD v1.2 §36 Wave G (P2) "reminder bulk actions". One POST sends a
 * reminder email to every active team whose registration is still
 * incomplete (server-computed, same `BR-156` completeness check as
 * everywhere else — never a client-selected recipient list).
 */
/** AC-371: the reminder email exactly as a team leader would get it (subject and body). Fetched only while the confirmation dialog is open. */
export function useRegistrationReminderPreview(competitionId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...competitionKeys.teams(competitionId), 'registration-reminder-preview'] as const,
    queryFn: () =>
      apiRequest<{
        subject: string
        text: string
        sampleTeamName: string
        recipientCount: number
      }>(`${base(competitionId)}/teams/registration-reminders/preview`),
    enabled: enabled && competitionId.length > 0,
    staleTime: 0,
    gcTime: 0,
  })
}

export function useSendRegistrationReminders(competitionId: string) {
  const query = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiRequest<RegistrationReminderResultDto>(
        `${base(competitionId)}/teams/registration-reminders`,
        {
          method: 'POST',
        },
      ),
    onSuccess: () => {
      void query.invalidateQueries({ queryKey: competitionKeys.teams(competitionId) })
    },
  })
}

// ---------------------------------------------------------------------------
// Global Competition Search (SDD v1.1 §14). Aggregated cross-entity
// quick-jump — distinct from `useTeams`'s paginated per-entity search above.
// ---------------------------------------------------------------------------

export const SEARCH_MIN_QUERY_LENGTH = 2

export interface CompetitionSearchTeamResult {
  type: 'team'
  id: string
  name: string
  code: string
  registrationStatus: 'complete' | 'incomplete' | 'cancelled'
}
export interface CompetitionSearchParticipantResult {
  type: 'participant'
  id: string
  teamId: string
  teamName: string
  displayName: string
  email: string
  eligibilityStatus: 'missing' | 'pending' | 'verified' | 'rejected' | 'expired'
}
export interface CompetitionSearchSubmissionResult {
  type: 'submission'
  id: string
  teamName: string
  status: string
}
export interface CompetitionSearchJudgeResult {
  type: 'judge'
  id: string
  displayName: string
  email: string
  invitationStatus: 'accepted'
}
export interface CompetitionSearchResponse {
  query: string
  results: {
    teams: CompetitionSearchTeamResult[]
    participants: CompetitionSearchParticipantResult[]
    submissions: CompetitionSearchSubmissionResult[]
    judges: CompetitionSearchJudgeResult[]
  }
}

/**
 * `enabled` gates on the same `SEARCH_MIN_QUERY_LENGTH` the server enforces
 * (`competition-search.ts`'s `searchQuerySchema`) — a shorter query never
 * even reaches the network, matching the debounced-autocomplete convention
 * `HelpSearchAutocomplete` already uses for its own `MIN_QUERY_LENGTH`.
 */
export function useCompetitionSearch(
  competitionId: string,
  query: string,
): UseQueryResult<CompetitionSearchResponse> {
  const trimmed = query.trim()
  return useQuery({
    queryKey: competitionKeys.search(competitionId, trimmed),
    queryFn: () =>
      apiRequest<CompetitionSearchResponse>(
        `${base(competitionId)}/search?q=${encodeURIComponent(trimmed)}`,
      ),
    enabled: competitionId.length > 0 && trimmed.length >= SEARCH_MIN_QUERY_LENGTH,
  })
}

export function useRemoveTeamMember(competitionId: string, teamId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (memberId: string) =>
      apiRequest(`${base(competitionId)}/teams/${teamId}/members/${memberId}`, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: competitionKeys.teamDetail(competitionId, teamId),
      })
    },
  })
}

export function useTransferTeamLeader(competitionId: string, teamId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: TransferTeamLeaderRequest) =>
      apiRequest(`${base(competitionId)}/teams/${teamId}/leader-transfer`, {
        method: 'POST',
        body,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: competitionKeys.teamDetail(competitionId, teamId),
      })
    },
  })
}

export function useTeamInvites(
  competitionId: string,
  teamId: string | null,
  enabled: boolean,
): UseQueryResult<TeamInviteDto[]> {
  return useQuery({
    queryKey: competitionKeys.teamInvites(competitionId, teamId ?? ''),
    queryFn: () => apiRequest<TeamInviteDto[]>(`${base(competitionId)}/teams/${teamId}/invites`),
    enabled: competitionId.length > 0 && teamId !== null && enabled,
  })
}

export function useCreateTeamInvite(competitionId: string, teamId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateTeamInviteRequest) =>
      apiRequest<TeamInviteCreatedDto>(`${base(competitionId)}/teams/${teamId}/invites`, {
        method: 'POST',
        body,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: competitionKeys.teamInvites(competitionId, teamId),
      })
    },
  })
}

export function useRevokeTeamInvite(competitionId: string, teamId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (inviteId: string) =>
      apiRequest(`${base(competitionId)}/teams/${teamId}/invites/${inviteId}`, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: competitionKeys.teamInvites(competitionId, teamId),
      })
    },
  })
}
